import { useEffect } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const clearMotionStyles = (targets: gsap.TweenTarget) => {
  gsap.set(targets, { clearProps: 'transform,opacity,visibility,filter,clipPath' });
};

export function useExperience(pathname: string) {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const lenis = new Lenis({
      duration: 0.8,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1.0,
      touchMultiplier: 1.5,
    });
    (window as any).__lenis = lenis;

    lenis.on('scroll', ScrollTrigger.update);
    const tickerUpdate = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(tickerUpdate);
    gsap.ticker.lagSmoothing(0);

    const ctx = gsap.context(() => {
      const entranceSelectors = ['.nav-shell', '.hero .eyebrow, .page-hero .eyebrow', '.hero-logo', '.hero h1, .page-hero h1', '.hero-content > p, .page-hero-copy > p', '.hero-actions, .language-switch', '.hero-stats > *, .reserve-seal, .constitution-seal'];
      const entranceNodes = entranceSelectors.flatMap((selector) => Array.from(document.querySelectorAll(selector)));
      const entrance = gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: () => clearMotionStyles(entranceNodes) });
      const addEntrance = (selector: string, vars: gsap.TweenVars, position?: string) => {
        const targets = document.querySelectorAll(selector);
        if (targets.length) entrance.from(targets, vars, position);
      };
      addEntrance('.nav-shell', { y: -36, autoAlpha: 0, duration: 0.65 });
      addEntrance('.hero .eyebrow, .page-hero .eyebrow', { x: -16, autoAlpha: 0, duration: 0.5 }, '-=.25');
      addEntrance('.hero-logo', { scale: 0.88, autoAlpha: 0, filter: 'blur(5px)', duration: 0.6 }, '-=.3');
      addEntrance('.hero h1, .page-hero h1', { y: 32, autoAlpha: 0, duration: 0.8, ease: 'power3.out' }, '-=.4');
      addEntrance('.hero-content > p, .page-hero-copy > p', { y: 16, autoAlpha: 0, duration: 0.6 }, '-=.35');
      addEntrance('.hero-actions, .language-switch', { y: 14, autoAlpha: 0, duration: 0.5 }, '-=.3');
      addEntrance('.hero-stats > *, .reserve-seal, .constitution-seal', { y: 12, autoAlpha: 0, stagger: 0.05, duration: 0.5 }, '-=.25');

      if (document.querySelector('.hero-bg')) {
        gsap.to('.hero-bg', {
          yPercent: 12,
          ease: 'none',
          scrollTrigger: {
            trigger: '.hero',
            start: 'top top',
            end: 'bottom top',
            scrub: true,
            invalidateOnRefresh: true,
          }
        });
      }

      document.querySelectorAll<HTMLElement>('.section-pad').forEach((section) => {
        const headerTargets = section.querySelectorAll('.section-label, .split-heading h2, .split-heading > p, .crossplay-copy h2, .crossplay-copy > p, .economy-cta h2, .economy-cta p');
        if (!headerTargets.length) return;
        gsap.from(headerTargets, {
          y: 22,
          autoAlpha: 0,
          stagger: 0.08,
          duration: 0.75,
          ease: 'power3.out',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: section, start: 'top 86%', once: true }
        });
      });

      const revealGroups: Array<[string, string, gsap.TweenVars]> = [
        ['.world-grid', '.world-image', { clipPath: 'inset(0 100% 0 0)', scale: 1.025 }],
        ['.world-copy', 'article', { x: 24 }],
        ['.reserve-grid', '.reserve-number > *, .money-stack img', { y: 24 }],
        ['.feature-row', ':scope > *', { y: 28 }],
        ['.industry-yards-grid', '.yard-card', { y: 26 }],
        ['.industry-map', '.yard, .map-center, .route-line', { scale: 0.92, y: 20 }],
        ['.gallery-showcase-grid', '.gallery-card', { y: 26 }],
        ['.plinths', ':scope > *', { y: 28 }],
        ['.join-steps', ':scope > *', { y: 16 }],
        ['.wallets', ':scope > *', { y: 28 }],
        ['.launder-flow', ':scope > *', { y: 16 }],
        ['.market-board', '.market-row, .embargo', { y: 16 }],
        ['.tax-tiers', ':scope > *', { y: 22 }],
        ['.debt-story', ':scope > *', { y: 22 }],
        ['.patent-grid', ':scope > *', { y: 24 }],
        ['.rule-list', '.rule-card', { y: 18 }]
      ];

      revealGroups.forEach(([groupSelector, childSelector, fromVars]) => {
        document.querySelectorAll<HTMLElement>(groupSelector).forEach((group) => {
          const targets = group.querySelectorAll<HTMLElement>(childSelector);
          if (!targets.length) return;
          gsap.from(targets, {
            ...fromVars,
            autoAlpha: 0,
            stagger: 0.08,
            duration: 0.7,
            ease: 'power3.out',
            clearProps: 'transform,opacity,visibility,clipPath,filter',
            scrollTrigger: { trigger: group, start: 'top 86%', once: true }
          });
        });
      });

      gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((element) => {
        if (element.closest('.section-pad')) return;
        gsap.from(element, {
          autoAlpha: 0,
          y: 20,
          duration: 0.7,
          ease: 'power3.out',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: { trigger: element, start: 'top 88%', once: true }
        });
      });

      gsap.utils.toArray<HTMLElement>('[data-parallax]:not(.hero-bg)').forEach((element) => {
        gsap.to(element, {
          yPercent: Math.min(Number(element.dataset.parallax || 8), 10),
          ease: 'none',
          scrollTrigger: { trigger: element.parentElement, start: 'top bottom', end: 'bottom top', scrub: true }
        });
      });

      requestAnimationFrame(() => ScrollTrigger.refresh());
      const onLoad = () => ScrollTrigger.refresh();
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    });

    return () => {
      gsap.ticker.remove(tickerUpdate);
      lenis.destroy();
      (window as any).__lenis = null;
      ctx.revert();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [pathname]);
}
